import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Icon } from '@evisort/eds/components/Icon/Icon';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { testIsActiveLink } from '~/eds/src/modules/PageHeader/utils';
import { NavLink } from '~/eds/src/types/NavLink';

import { readCssDurationMs } from './readCssDurationMs';
import { getSidebarAnalyticsItem } from './sidebarAnalytics';
import { SidebarNavControl } from './SidebarNavControl';
import { SidebarPinButton } from './SidebarPinButton';

export const messages = defineMessages({
  heading: {
    id: 'AppSidebarNav.pinnedHeading',
    defaultMessage: 'Pinned',
    description: 'Overline above the pinned entries at the top of the sidebar.',
  },
  count: {
    id: 'AppSidebarNav.pinnedCount',
    defaultMessage: '{count} pinned entries',
    description: 'Accessible label for the count of pinned sidebar entries.',
  },
  reorder: {
    id: 'AppSidebarNav.reorderPinnedItem',
    defaultMessage: 'Reorder {label}',
    description:
      'Accessible label for the handle that drags a pinned entry to a new position.',
  },
});

interface PinnedRow {
  link: NavLink;
  isEntering: boolean;
  isLeaving: boolean;
}

function haveSameRows(a: PinnedRow[], b: PinnedRow[]) {
  return (
    a.length === b.length &&
    a.every(
      (row, index) =>
        row.link.pathname === b[index].link.pathname &&
        row.isEntering === b[index].isEntering &&
        row.isLeaving === b[index].isLeaving,
    )
  );
}

/**
 * An unpinned entry holds its place, marked as leaving, until its exit has
 * played — otherwise the row it sat in is gone on the next paint and the rows
 * below jump up. It stays at the index it had, so the list doesn't resort
 * around a row that is on its way out.
 *
 * Only a pathname that wasn't here before is marked as entering. Reordering
 * moves rows the list already had, so dropping one settles it into its new
 * place instead of replaying every row's arrival.
 */
function reconcilePinnedRows(previous: PinnedRow[], links: NavLink[]) {
  const known = new Set(previous.map((row) => row.link.pathname));
  const pinned = new Set(links.map((link) => link.pathname));
  const next: PinnedRow[] = links.map((link) => ({
    link,
    isEntering: !known.has(link.pathname),
    isLeaving: false,
  }));

  previous.forEach((row, index) => {
    if (pinned.has(row.link.pathname)) return;
    next.splice(Math.min(index, next.length), 0, {
      ...row,
      isEntering: false,
      isLeaving: true,
    });
  });

  return haveSameRows(previous, next) ? previous : next;
}

/**
 * The rows to render, including the ones animating out, and whether the
 * section itself is on its way out — unpinning the last entry plays the
 * section's exit rather than snapping it away mid-animation.
 */
function usePinnedSectionPresence(
  links: NavLink[],
  sectionRef: RefObject<HTMLElement | null>,
) {
  // Rows already pinned when the section mounts arrive with it, so they don't
  // animate on their own — the section's own entrance covers them.
  const [rows, setRows] = useState<PinnedRow[]>(() =>
    links.map((link) => ({ link, isEntering: false, isLeaving: false })),
  );

  useEffect(() => {
    setRows((previous) => reconcilePinnedRows(previous, links));
  }, [links]);

  useEffect(() => {
    if (!rows.some((row) => row.isEntering)) return;

    const timeout = setTimeout(
      () =>
        setRows((previous) =>
          previous.map((row) =>
            row.isEntering ? { ...row, isEntering: false } : row,
          ),
        ),
      readCssDurationMs(sectionRef.current, '--sidebar-reveal-row-enter'),
    );
    return () => clearTimeout(timeout);
  }, [rows, sectionRef]);

  useEffect(() => {
    if (!rows.some((row) => row.isLeaving)) return;

    const timeout = setTimeout(
      () => setRows((previous) => previous.filter((row) => !row.isLeaving)),
      // The section's exit is the longer of the two, so a row waiting on it
      // outlives its own.
      readCssDurationMs(sectionRef.current, '--sidebar-reveal-exit'),
    );
    return () => clearTimeout(timeout);
  }, [rows, sectionRef]);

  return { isLeaving: links.length === 0 && rows.length > 0, rows };
}

/** Settles the dragged row's lift instead of cutting it on drop. */
const LIFT_TRANSITION = 'box-shadow 160ms ease, background-color 160ms ease';

interface SidebarPinnedItemProps {
  link: NavLink;
  activePathname: string;
  isEntering: boolean;
  isLeaving: boolean;
  onActivate: (path: string) => void;
  onTogglePin: (pathname: string) => void;
}

function SidebarPinnedItem({
  link,
  activePathname,
  isEntering,
  isLeaving,
  onActivate,
  onTogglePin,
}: SidebarPinnedItemProps) {
  const intl = useIntl();
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: link.pathname });

  const isActive = testIsActiveLink(activePathname)(link);

  return (
    <div
      ref={setNodeRef}
      aria-hidden={isLeaving}
      className={`sidebar-pinned-row${isActive ? ' sidebar-pinned-row--active' : ''}${isDragging ? ' sidebar-pinned-row--dragging' : ''}${isEntering ? ' sidebar-pinned-row--entering' : ''}${isLeaving ? ' sidebar-pinned-row--leaving' : ''}`}
      style={{
        transform: transform
          ? `translate3d(0, ${transform.y}px, 0)`
          : undefined,
        // dnd-kit owns `transition` while sorting, and an inline value replaces
        // the stylesheet's outright — so the lift's fade has to ride along with
        // it, or the shadow snaps off the moment a row lands.
        transition: [transition, LIFT_TRANSITION].filter(Boolean).join(', '),
      }}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        aria-label={intl.formatMessage(messages.reorder, { label: link.label })}
        className="sidebar-pinned-drag-handle"
        {...attributes}
        {...listeners}
      >
        <Icon icon="drag-and-drop-indicator" size="s" />
      </button>
      <SidebarNavControl
        analyticsItem={getSidebarAnalyticsItem(link.pathname)}
        className="sidebar-pinned-item-btn"
        isActive={isActive}
        pathname={link.pathname}
        onActivate={onActivate}
      >
        <span className="sidebar-pinned-item-label">{link.label}</span>
      </SidebarNavControl>
      <SidebarPinButton
        isPinned
        label={link.label}
        onToggle={() => onTogglePin(link.pathname)}
      />
    </div>
  );
}

interface SidebarPinnedSectionProps {
  links: NavLink[];
  activePathname: string;
  onActivate: (path: string) => void;
  onReorder: (fromPathname: string, toPathname: string) => void;
  onTogglePin: (pathname: string) => void;
}

/**
 * The pinned entries, above every other section. Only shows once something is
 * pinned — an empty "Pinned" heading would be permanent chrome for the people
 * who never pin anything — and opens and closes on the transitions rather than
 * appearing and vanishing under the rest of the nav.
 */
export function SidebarPinnedSection({
  links,
  activePathname,
  onActivate,
  onReorder,
  onTogglePin,
}: SidebarPinnedSectionProps) {
  const intl = useIntl();
  const sectionRef = useRef<HTMLDivElement>(null);
  const { isLeaving, rows } = usePinnedSectionPresence(links, sectionRef);
  const sensors = useSensors(
    // A short drag threshold so a plain click still navigates the link.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  if (rows.length === 0) return null;

  // Only the rows still pinned take part in a drag; a leaving row keeps its
  // slot but nothing sorts around it.
  const pathnames = links.map((link) => link.pathname);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  };

  return (
    <div
      ref={sectionRef}
      aria-hidden={isLeaving}
      className={`sidebar-pinned-section sidebar-reveal${isLeaving ? ' sidebar-reveal--leaving' : ''}`}
      data-amp-track-section="pinned-nav"
    >
      <div className="sidebar-reveal-content">
        <div className="sidebar-pinned-header">
          <span className="sidebar-overline">
            {intl.formatMessage(messages.heading)}
          </span>
          <span
            aria-label={intl.formatMessage(messages.count, {
              count: links.length,
            })}
            className="sidebar-pinned-count"
          >
            <Icon filled icon="pin" size="s" />
            {intl.formatNumber(links.length)}
          </span>
        </div>
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          sensors={sensors}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={pathnames}
            strategy={verticalListSortingStrategy}
          >
            {rows.map((row) => (
              <SidebarPinnedItem
                key={row.link.pathname}
                link={row.link}
                activePathname={activePathname}
                isEntering={row.isEntering}
                isLeaving={row.isLeaving}
                onActivate={onActivate}
                onTogglePin={onTogglePin}
              />
            ))}
          </SortableContext>
        </DndContext>
        <div
          aria-hidden="true"
          className="sidebar-section-divider"
          data-testid="sidebar-pinned-divider"
        />
      </div>
    </div>
  );
}
